/**
 * src/AudioEngine.js
 *
 * The main public API for haptic-audio consumers.
 * Thin orchestration layer — all DSP lives in the voice classes.
 *
 * Usage:
 *   import { AudioEngine } from 'haptic-audio';
 *
 *   const engine = new AudioEngine({ masterVolume: 0.75 });
 *
 *   // Built-in voices:
 *   engine.startFluxHover(speedRatio);  // WaterFlowVoice
 *   engine.startCenterHover(ratio);     // HeartbeatVoice
 *   engine.updateRatio(newRatio);
 *   engine.stopHover();
 *
 *   // Generic voice API (for custom voices):
 *   engine.registry.register('myVoice', MyVoiceClass);
 *   engine.startVoice('myVoice', params, liveParam);
 *   engine.updateVoice(newLiveParam);
 *   engine.stopVoice();
 *
 *   engine.fireGranularBurst(ratio);
 *   engine.setVolume(0.5);
 *   engine.dispose();
 */

import { AudioContextManager } from "./core/AudioContextManager.js";
import { VoiceRegistry } from "./core/VoiceRegistry.js";
import { WaterFlowVoice } from "./voices/WaterFlowVoice.js";
import { HeartbeatVoice } from "./voices/HeartbeatVoice.js";
import { RecipeVoice } from "./voices/RecipeVoice.js";
import { fireGranularBurst } from "./voices/GranularVoice.js";
import { clamp01 } from "./utils/math.js";

export class AudioEngine {
    /**
     * @param {object}       cfg
     * @param {number}       [cfg.masterVolume=0.8]
     * @param {AudioContext} [cfg.audioContext]    - Inject an existing AudioContext
     * @param {object}       [cfg.waterFlow]       - WaterFlowVoice param overrides
     * @param {object}       [cfg.heartbeat]       - HeartbeatVoice param overrides
     * @param {object}       [cfg.recipe]          - RecipeVoice param overrides
     * @param {object}       [cfg.granular]        - fireGranularBurst cfg overrides
     */
    constructor(cfg = {}) {
        // AudioContextManager handles singleton, lifecycle listeners, and iOS resume
        this.ctx = AudioContextManager.get(cfg.audioContext);

        this.masterGain = this.ctx.createGain();
        this.masterGain.gain.setValueAtTime(
            cfg.masterVolume ?? 0.8,
            this.ctx.currentTime,
        );

        // AnalyserNode sits between masterGain and destination so the demo (and any
        // consumer visualizer) can tap the signal without a separate connection.
        // Chain: voices → masterGain → analyser → destination
        this.analyser = this.ctx.createAnalyser();
        this.analyser.fftSize = 8192;
        this.analyser.smoothingTimeConstant = 0.8;

        this.masterGain.connect(this.analyser);
        this.analyser.connect(this.ctx.destination);

        // Registry pre-loaded with built-in voices
        this.registry = new VoiceRegistry();
        this.registry
            .register("waterFlow", WaterFlowVoice)
            .register("heartbeat", HeartbeatVoice)
            .register("recipe", RecipeVoice);

        // Per-voice config overrides (merged at start time, not construction time,
        // so consumers can mutate them between calls)
        this._cfg = {
            waterFlow: cfg.waterFlow ?? {},
            heartbeat: cfg.heartbeat ?? {},
            recipe: cfg.recipe ?? {},
            granular: cfg.granular ?? {},
        };
    }

    // ── Context lifecycle ─────────────────────────────────────────────────────

    /**
     * Resume the AudioContext — must be called from a user-gesture handler.
     * AudioEngine calls this automatically from startFluxHover / startCenterHover,
     * so you only need to call it directly if you use the generic startVoice API.
     *
     * @returns {Promise<void>}
     */
    resume() {
        return AudioContextManager.resume(this.ctx);
    }

    // ── Named convenience methods (for Scene.jsx / original API) ─────────────

    /**
     * Start the WaterFlowVoice on a pipe hover.
     * If a WaterFlowVoice is already playing, just updates it — no click.
     *
     * @param {number} speedRatio  load / capaMax ∈ [0,1]
     */
    startFluxHover(speedRatio) {
        this.resume();
        if (this.registry.activeName === "waterFlow") {
            this.registry.update(speedRatio);
            return;
        }
        this.registry.start(
            "waterFlow",
            this.ctx,
            this.masterGain,
            { ...WaterFlowVoice.defaultParams(), ...this._cfg.waterFlow },
            speedRatio,
        );
    }

    /**
     * Start the HeartbeatVoice on a centre node hover.
     *
     * @param {number} ratio  (level − levelMin) / (levelMax − levelMin) ∈ [0,1]
     */
    startCenterHover(ratio) {
        this.resume();
        if (this.registry.activeName === "heartbeat") {
            this.registry.update(ratio);
            return;
        }
        this.registry.start(
            "heartbeat",
            this.ctx,
            this.masterGain,
            { ...HeartbeatVoice.defaultParams(), ...this._cfg.heartbeat },
            ratio,
        );
    }

    /**
     * Update the active voice's live parameter while the cursor is still hovering.
     * Call on throttled pointermove.
     *
     * @param {number} ratio
     */
    updateRatio(ratio) {
        this.registry.update(ratio);
    }

    /** Stop whichever voice is currently playing. Call on pointerleave. */
    stopHover() {
        this.registry.stopActive();
    }

    // ── Generic voice API (for custom voices / the demo) ─────────────────────

    /**
     * Start any registered voice by name.
     *
     * @param {string} name
     * @param {object} params     - voice cfg (will be merged with defaultParams)
     * @param {number} liveParam  - initial live parameter
     */
    startVoice(name, params, liveParam) {
        this.resume();
        const defaults = this.registry.defaultParams(name);
        this.registry.start(
            name,
            this.ctx,
            this.masterGain,
            { ...defaults, ...params },
            liveParam,
        );
    }

    /** Update live parameter on the currently active voice. */
    updateVoice(liveParam) {
        this.registry.update(liveParam);
    }

    /** Stop the currently active voice. */
    stopVoice(onDone) {
        this.registry.stopActive(onDone);
    }

    // ── Granular one-shot ─────────────────────────────────────────────────────

    /**
     * Fire a one-shot granular burst (Z-scale tick feedback).
     *
     * @param {number} ratio  position within domain ∈ [0,1] → pitch + density
     */
    fireGranularBurst(ratio) {
        this.resume();
        fireGranularBurst(this.ctx, this.masterGain, ratio, this._cfg.granular);
    }

    // ── Master gain ───────────────────────────────────────────────────────────

    /**
     * Smoothly set master volume.
     * @param {number} volume  0–1
     */
    setVolume(volume) {
        const now = this.ctx.currentTime;
        this.masterGain.gain.cancelScheduledValues(now);
        this.masterGain.gain.setValueAtTime(this.masterGain.gain.value, now);
        this.masterGain.gain.linearRampToValueAtTime(
            clamp01(volume),
            now + 0.05,
        );
    }

    // ── Cleanup ───────────────────────────────────────────────────────────────

    /**
     * Tear down everything. Call in React's useEffect cleanup / component unmount.
     */
    dispose() {
        this.registry.stopActive();
        try {
            this.masterGain.disconnect();
        } catch (_) {}
        try {
            this.analyser.disconnect();
        } catch (_) {}
        AudioContextManager.close();
    }
}
